import { useState } from 'react';
import { Link, useInRouterContext } from 'react-router-dom';
import type { FavoriteLineDto } from '../../api/approval';
import { Badge, Button, Input } from '../../components/shared';
import { GroupedSelect } from '../../components/shared/GroupedSelect';
import { InlineNote } from '../../components/shared/InlineNote';
import { Switch } from '../../components/shared/Switch';
import type { ApprovalLineValue } from './ApprovalLineBlock';
import { favoriteBadge, favoriteGroups, findSameName, lineSummary } from './favorite-line-view';
import './favorite-line.css';

/**
 * 즐겨찾는 결재선 — 고르개 · 되돌리기 · 저장 창 (2026-09-15 · 대시보드 시안 v2 V9 · 정본 제안 B11-5 · F1-4).
 *
 * 캡처: `_workspace/design-dashboard-v2/attendance-favline-open.png` · `-picked` · `-save` · `-empty` · `attendance-trip-tab.png`.
 * `ApprovalLineBlock` 이 `favoritesVariant="select"` 일 때만 쓴다 — 기본(`inline`)은 종전 「자주 쓰는 결재선」 줄이다.
 *
 * ★★★ **저장 창은 모달 안에 «펼치는 칸»으로 둔다** — 공용 `Modal` 을 하나 더 띄우면 두 창이 같은 window Escape 를
 *     받아 **둘 다 닫히고**, 포커스 가두기도 둘이 싸운다. 시안의 겹친 작은 창 대신 같은 내용 · 같은 순서를 제자리에 편다.
 * ★★ 「결재선 관리」 링크는 **회사 관리자에게만**(관제 Q-7) — 개인 결재선 관리 화면은 없다. 일반 사용자는 목록 줄의 × 로 지운다.
 * ★ 「회사 공용으로 저장」 스위치도 관리자에게만 — 화면이 가리는 것은 친절이고 막는 것은 서버다.
 */

export const APPROVAL_LINES_ADMIN_PATH = '/approval/admin/lines';

function ManageLink() {
  // 라우터 밖(단위 시험 · 떠 있는 창)에서도 깨지지 않게 — 라우터 안이면 화면 이동, 밖이면 보통 링크.
  const inRouter = useInRouterContext();
  return inRouter
    ? <Link className="aprl-fav__manage" to={APPROVAL_LINES_ADMIN_PATH}>결재선 관리</Link>
    : <a className="aprl-fav__manage" href={APPROVAL_LINES_ADMIN_PATH}>결재선 관리</a>;
}

export function FavoriteLineSelect({
  docType, favorites, sourceId, loading = false, canManage, disabled, onPick, onRemove, onOpenSave,
}: {
  docType: string;
  favorites: readonly FavoriteLineDto[];
  /** 지금 결재선이 어느 즐겨찾기에서 왔나 — 손으로 고쳤으면 null. */
  sourceId: string | null;
  /** 목록을 아직 다 못 읽었다 — 「없음」이라고 말하지 않고 잠시 막아 둔다. */
  loading?: boolean;
  canManage: boolean;
  disabled?: boolean;
  onPick: (fav: FavoriteLineDto) => void;
  onRemove: (fav: FavoriteLineDto) => void;
  onOpenSave: () => void;
}) {
  const groups = favoriteGroups(favorites, docType);
  const selected = favorites.find((f) => f.id === sourceId) ?? null;
  const badge = selected ? favoriteBadge(selected, docType) : null;
  const empty = !loading && favorites.length === 0;

  return (
    <div className="aprl-fav">
      <GroupedSelect
        ariaLabel="즐겨찾는 결재선"
        value={selected?.id ?? null}
        groups={groups}
        disabled={disabled || loading || empty}
        summary={selected
          ? lineSummary(selected.approvers, selected.referrers, selected.missing)
          : empty ? '저장한 결재선이 생기면 여기서 고릅니다' : undefined}
        onChange={(id) => {
          const fav = favorites.find((f) => f.id === id);
          if (fav) onPick(fav);
        }}
        onRemove={(o) => {
          const fav = favorites.find((f) => f.id === o.value);
          if (fav) onRemove(fav);
        }}
        removeLabel={(o) => `${o.label} 지우기`}
        renderValue={() => (selected
          ? (
            <span className="aprl-fav__value">
              즐겨찾는 결재선 · <b>{selected.name}</b>
              {badge && <Badge size="sm" variant={badge === '회사 기본' ? 'primary' : 'default'}>{badge}</Badge>}
            </span>
          )
          : (
            <span className="aprl-fav__placeholder">
              {loading ? '즐겨찾는 결재선 불러오는 중…' : empty ? '즐겨찾는 결재선 없음' : '즐겨찾는 결재선 고르기'}
            </span>
          ))}
        footer={(close) => (
          <>
            <Button size="sm" variant="soft" onClick={() => { close(); onOpenSave(); }}>
              지금 결재선을 즐겨찾기로 저장
            </Button>
            {canManage && <ManageLink />}
          </>
        )}
      />
    </div>
  );
}

export function FavoriteLineAfter({
  value, favorites, undoName, saveOpen, canShare, disabled, showEmptyNote,
  onUndo, onOpenSave, onCloseSave, onSave,
}: {
  value: ApprovalLineValue;
  favorites: readonly FavoriteLineDto[];
  /** 방금 채운 즐겨찾기 이름 — 있으면 「되돌리기」가 선다. */
  undoName: string | null;
  saveOpen: boolean;
  canShare: boolean;
  disabled?: boolean;
  /** 저장한 결재선도 회사 기본 결재선도 없고 결재자도 비었다. */
  showEmptyNote: boolean;
  onUndo: () => void;
  onOpenSave: () => void;
  onCloseSave: () => void;
  /** 저장 — 성공하면 true(부르는 쪽이 목록을 다시 읽고 창을 닫는다). */
  onSave: (input: { name: string; shared: boolean }) => Promise<boolean>;
}) {
  const [name, setName] = useState('');
  const [shared, setShared] = useState(false);
  const [saving, setSaving] = useState(false);

  const dup = findSameName(favorites, name, shared);
  const canSave = !disabled && !saving && name.trim() !== '' && value.approvers.length > 0;

  const closePanel = () => {
    setName('');
    setShared(false);
    onCloseSave();
  };

  const submit = async () => {
    if (!canSave) return;
    setSaving(true);
    const ok = await onSave({ name: name.trim(), shared });
    setSaving(false);
    if (ok) { setName(''); setShared(false); }
  };

  return (
    <>
      {undoName && (
        <p className="aprl-fav__hint" role="status">
          「{undoName}」로 결재자와 참고자를 채웠습니다 ·{' '}
          <button type="button" className="aprl-fav__link" onClick={onUndo} disabled={disabled}>되돌리기</button>
        </p>
      )}

      {showEmptyNote && (
        <InlineNote tone="info">
          <b>저장한 결재선이 없고, 이 양식에는 회사 기본 결재선도 없습니다.</b>{' '}
          결재자를 고른 뒤 「지금 결재선을 즐겨찾기로 저장」을 누르면 다음부터 여기서 불러옵니다.
        </InlineNote>
      )}

      {!saveOpen && (
        <p className="aprl-fav__hint">
          지금 결재선을{' '}
          <button type="button" className="aprl-fav__link" onClick={onOpenSave} disabled={disabled}>즐겨찾기로 저장</button>
          {' '}· 같은 이름이면 덮어씁니다
        </p>
      )}

      {saveOpen && (
        <div
          className="aprl-fav__panel"
          role="group"
          aria-label="즐겨찾는 결재선으로 저장"
          onKeyDown={(e) => {
            // 칸만 접는다 — 멈추지 않으면 공용 Modal 이 window Escape 로 창을 통째로 닫는다.
            if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); closePanel(); }
          }}
        >
          <b className="aprl-fav__panel-title">즐겨찾는 결재선으로 저장</b>
          <Input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="예) 내 휴가 — 팀장 바로"
            maxLength={100}
            aria-label="결재선 이름"
            autoFocus
          />
          {dup && (
            <InlineNote tone="warning">
              같은 이름의 {dup.shared ? '회사 공용' : '내'} 결재선이 이미 있습니다 — 저장하면 지금 결재선으로 덮어씁니다
            </InlineNote>
          )}
          <p className="aprl-fav__preview">
            {value.approvers.length > 0 ? lineSummary(value.approvers, value.referrers) : '결재자를 먼저 고르세요'}
          </p>
          {canShare && (
            <Switch
              checked={shared}
              onCheckedChange={setShared}
              disabled={disabled}
              label="회사 공용으로 저장 — 관리자만 · 모두가 함께 씁니다"
            />
          )}
          <div className="aprl-fav__actions">
            <Button size="sm" variant="soft" onClick={closePanel}>취소</Button>
            <Button size="sm" disabled={!canSave} onClick={() => { void submit(); }}>
              {saving ? '저장 중…' : dup ? '덮어쓰기' : '저장'}
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
